import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';

interface RecruiterGateCardProps {
  icon: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  /** Center within a page shell (default) or fill viewport when standalone. */
  fullScreen?: boolean;
}

/**
 * Frosted conversion / gate card — signed-out, explorer gate, no-company, etc.
 * Matches Hiring Board logged-out aesthetic.
 */
export function RecruiterGateCard({
  icon,
  title,
  description,
  children,
  className,
  fullScreen = false,
}: RecruiterGateCardProps) {
  const card = (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.99 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={cn(
        'relative w-full max-w-lg rounded-[28px] border border-white/70 bg-white/80 p-8 text-center shadow-[0_12px_50px_-12px_rgba(15,23,42,0.18)] backdrop-blur-xl sm:p-10',
        className,
      )}
    >
      <div className="mx-auto mb-6 flex size-12 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-500 to-rose-500 shadow-lg shadow-pink-500/25">
        {icon}
      </div>
      <h3 className="text-2xl font-semibold tracking-tight text-slate-900">{title}</h3>
      {description && (
        <p className="mt-2 text-[15px] leading-relaxed text-slate-500">{description}</p>
      )}
      {children}
    </motion.div>
  );

  if (fullScreen) {
    return (
      <div className="flex min-h-[calc(100vh-120px)] items-center justify-center px-4 py-12">
        {card}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 sm:py-24 px-4">
      {card}
    </div>
  );
}
